"use client";

import { Skill } from "@/modules/types/metadata/metadata";
import { Button } from "@headlessui/react";
import { ArrowDown01Icon } from "hugeicons-react";
import { useEffect, useRef, useState } from "react";

const DisplaySkills = ({ skills }: { skills?: Skill[] }) => {
  const [showMore, setShowMore] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [overflowCount, setOverflowCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkOverflow();

    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [skills]);

  const toggleShowMore = () => {
    if (containerRef.current) {
      containerRef.current.style.maxHeight = "";
    }
    setShowMore(!showMore);
  };

  // Use requestAnimationFrame to ensure DOM is painted
  const checkOverflow = () => {
    if (containerRef.current) {
      // Reset height temporarily to get the full scroll height
      containerRef.current.style.maxHeight = "none";
      const fullHeight = containerRef.current.scrollHeight;

      // Reset back to collapsed state
      containerRef.current.style.maxHeight = "2rem"; // equivalent to max-h-8

      // Check if content would overflow in collapsed state
      const hasContentOverflow = fullHeight > 32; // 2rem = 32px
      setHasOverflow(hasContentOverflow);

      if (!skills || skills.length <= 0) return;
      // Count And set invisible skills
      const spans = containerRef.current.getElementsByTagName("span");
      let visibleCount = 0;
      // Get container boundaries
      const containerTop = containerRef.current.getBoundingClientRect().top;
      const containerBottom = containerTop + 32; // max-h-8 = 2rem = 32px
      // Count visible skills
      Array.from(spans).forEach((span) => {
        const rect = span.getBoundingClientRect();
        if (rect.top < containerBottom) {
          visibleCount++;
        }
      });

      const overflowed = skills.length - visibleCount;
      setOverflowCount(overflowed);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full flex flex-wrap gap-2 pe-14 ${!showMore ? "max-h-8 overflow-hidden" : "h-fit"}`}
    >
      {skills &&
        skills.map((skill, index) => (
          <span
            key={index}
            className="bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-white px-2 py-1 rounded-md"
          >
            {skill.name}
          </span>
        ))}
      {hasOverflow && overflowCount > 0 && (
        <Button
          onClick={toggleShowMore}
          className="absolute flex justify-center items-center top-0 right-5 bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-white px-2 py-1 rounded-md data-[hover]:bg-gray-300 dark:data-[hover]:bg-white/50 transition-colors duration-300"
        >
          +{overflowCount}
          {showMore ? (
            <ArrowDown01Icon className="size-4 rotate-180" />
          ) : (
            <ArrowDown01Icon className="size-4" />
          )}
        </Button>
      )}
    </div>
  );
};

DisplaySkills.displayName = "DisplaySkills";
export default DisplaySkills;
